import React, { useState, useEffect } from 'react';
import {
  CogIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  BeakerIcon,
  LightBulbIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

import { feedStockApi, FeedStock } from '../api/feedStock';
import { nutritionApi, NutritionalRequirements, OptimizedFeedMix, DailyFeedMix } from '../api/nutrition';
import DecimalInput from './ui/DecimalInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from './ui/BrandModal';
import { buildCreateMixFromOptimization } from './nutrition/nutritionMixBridge';
import { toast } from 'react-hot-toast';

interface FeedMixOptimizerProps {
  onOptimize: () => void;
  isModal?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  selectedLocation?: string;
  onOpenInMixForm?: (initialData: Partial<import('../api/nutrition').CreateDailyFeedMixDto>) => void;
  onCreatedForDispense?: (mix: DailyFeedMix) => void;
}

export default function FeedMixOptimizer({
  onOptimize,
  isModal = false,
  isOpen = true,
  onClose,
  selectedLocation = '',
  onOpenInMixForm,
  onCreatedForDispense,
}: FeedMixOptimizerProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [feedStocks, setFeedStocks] = useState<FeedStock[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [optimizationResult, setOptimizationResult] = useState<OptimizedFeedMix | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    targetDate: format(new Date(), 'yyyy-MM-dd'),
    targetQuantity: 10,
    optimizationGoal: 'balanced' as 'cost' | 'nutrition' | 'balanced',
    maxComponentPercentage: 60,
    availableFeedStockIds: [] as string[],
    requirements: {
      minProteinPercentage: 15,
      maxProteinPercentage: 20,
      minFatPercentage: 4,
      maxFatPercentage: 8,
      maxFiberPercentage: 15,
      maxMoisturePercentage: 12,
      minCaloriesPerKg: 3500,
      birdSpecies: 'Mixed',
      lifestage: 'maintenance' as 'breeding' | 'maintenance' | 'molting' | 'juvenile'
    } as NutritionalRequirements
  });

  // Load feed stocks
  useEffect(() => {
    const loadFeedStocks = async () => {
      try {
        const response = await feedStockApi.getFeedStocks();
        const stocks = response.data.filter(stock => stock.quantity > 0); // Only available stocks
        setFeedStocks(stocks);
        // Pre-select all available stocks
        setFormData(prev => ({
          ...prev,
          availableFeedStockIds: stocks.map(stock => stock.id)
        }));
      } catch (err: any) {
        setError('Failed to load feed stocks');
      }
    };
    loadFeedStocks();
  }, []);

  const handleOptimize = async (e: React.FormEvent) => {
    e.preventDefault();

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    if (formData.availableFeedStockIds.length === 0) {
      setError('Please select at least one feed stock');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await nutritionApi.optimizeFeedMix({
        targetDate: formData.targetDate,
        requirements: formData.requirements,
        availableFeedStockIds: formData.availableFeedStockIds,
        targetQuantity: formData.targetQuantity,
        optimizationGoal: formData.optimizationGoal,
        maxComponentPercentage: formData.maxComponentPercentage
      });

      setOptimizationResult(result);
      onOptimize();
    } catch (err: any) {
      setError(err.message || 'Failed to optimize feed mix');
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFeedStock = (feedStockId: string) => {
    setFormData(prev => ({
      ...prev,
      availableFeedStockIds: prev.availableFeedStockIds.includes(feedStockId)
        ? prev.availableFeedStockIds.filter(id => id !== feedStockId)
        : [...prev.availableFeedStockIds, feedStockId]
    }));
  };

  const getRequirementStatus = (value: number, min?: number, max?: number) => {
    if (min !== undefined && value < min) return 'low';
    if (max !== undefined && value > max) return 'high';
    return 'good';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good':
        return 'text-green-600 bg-green-100';
      case 'low':
        return 'text-red-600 bg-red-100';
      case 'high':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-slate-600 bg-gray-100';
    }
  };

  const resetForm = () => {
    setOptimizationResult(null);
    setError(null);
  };

  const presetRequirements = {
    breeding: {
      minProteinPercentage: 18,
      maxProteinPercentage: 22,
      minFatPercentage: 5,
      maxFatPercentage: 16,
      maxFiberPercentage: 12,
      maxMoisturePercentage: 12,
      minCaloriesPerKg: 4000,
    },
    maintenance: {
      minProteinPercentage: 14,
      maxProteinPercentage: 18,
      minFatPercentage: 3,
      maxFatPercentage: 16,
      maxFiberPercentage: 18,
      maxMoisturePercentage: 12,
      minCaloriesPerKg: 3500,
    },
    molting: {
      minProteinPercentage: 20,
      maxProteinPercentage: 24,
      minFatPercentage: 4,
      maxFatPercentage: 16,
      maxFiberPercentage: 15,
      maxMoisturePercentage: 12,
      minCaloriesPerKg: 3800,
    },
    juvenile: {
      minProteinPercentage: 16,
      maxProteinPercentage: 20,
      minFatPercentage: 4,
      maxFatPercentage: 16,
      maxFiberPercentage: 16,
      maxMoisturePercentage: 12,
      minCaloriesPerKg: 3700,
    }
  };

  const handleLifestageChange = (lifestage: string) => {
    const preset = presetRequirements[lifestage as keyof typeof presetRequirements];
    if (preset) {
      setFormData(prev => ({
        ...prev,
        requirements: {
          ...prev.requirements,
          ...preset,
          lifestage: lifestage as any
        }
      }));
    }
  };

  const optimizerSnapshot = () => ({
    targetDate: formData.targetDate,
    targetQuantity: formData.targetQuantity,
    location: selectedLocation || undefined,
    optimizationGoal: formData.optimizationGoal,
  });

  const buildOptimizationPayload = () => ({
    targetDate: formData.targetDate,
    requirements: formData.requirements,
    availableFeedStockIds: formData.availableFeedStockIds,
    targetQuantity: formData.targetQuantity,
    optimizationGoal: formData.optimizationGoal,
    maxComponentPercentage: formData.maxComponentPercentage,
    location: selectedLocation?.trim() || undefined,
  });

  const apiErrorMessage = (err: unknown, fallback: string) => {
    const e = err as { data?: { message?: string | string[] }; message?: string };
    const raw = e?.data?.message ?? e?.message;
    if (Array.isArray(raw)) return raw.join(', ');
    return raw || fallback;
  };

  const handleOpenInMixForm = () => {
    if (!optimizationResult || !onOpenInMixForm) return;
    onOpenInMixForm(
      buildCreateMixFromOptimization(optimizationResult, optimizerSnapshot(), {
        location: selectedLocation || undefined,
      }),
    );
    if (isModal && onClose) onClose();
  };

  const handleCreateForDispense = async (autoApprove: boolean) => {
    if (!optimizationResult) return;
    try {
      toast.loading(autoApprove ? 'Creating approved mix for field…' : 'Creating mix for dispensing…', {
        id: 'create-mix',
      });
      const result = await nutritionApi.createFromOptimization({
        ...buildOptimizationPayload(),
        autoApprove,
        approvedBy: autoApprove ? 'Nutrition Management' : undefined,
        replaceExisting: true,
      });
      toast.success(
        autoApprove
          ? 'Mix created and approved — ready for field dispensing'
          : 'Mix created — approve it in Records before field dispense',
        { id: 'create-mix' },
      );
      onCreatedForDispense?.(result.mix);
      onOptimize();
      if (isModal && onClose) onClose();
    } catch (err: unknown) {
      toast.error(apiErrorMessage(err, 'Failed to create mix from optimization'), { id: 'create-mix' });
    }
  };

  const optimizerBody = (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleOptimize}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Parameters */}
          <div className="space-y-6">
            {/* Basic Parameters */}
            <div className="bg-white rounded-lg shadow p-6">
              <h4 className="text-lg font-medium text-slate-900 mb-4">Optimization Parameters</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700">Target Date</label>
                  <input
                    type="date"
                    value={formData.targetDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetDate: e.target.value }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
                <DecimalInput
                  label="Target Quantity (kg)"
                  value={formData.targetQuantity}
                  onChange={(value) => setFormData((prev) => ({ ...prev, targetQuantity: value ?? 0 }))}
                  min={0.1}
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700">Optimization Goal</label>
                  <select
                    value={formData.optimizationGoal}
                    onChange={(e) => setFormData(prev => ({ ...prev, optimizationGoal: e.target.value as any }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    <option key="balanced-1753461297312" value="balanced">Balanced (Cost + Nutrition)</option>
                    <option key="cost-1753461297312" value="cost">Minimize Cost</option>
                    <option key="nutrition-1753461297312" value="nutrition">Maximize Nutrition</option>
                  </select>
                </div>
                <DecimalInput
                  label="Max Component % (per ingredient)"
                  value={formData.maxComponentPercentage}
                  onChange={(value) => setFormData((prev) => ({ ...prev, maxComponentPercentage: value ?? 0 }))}
                  min={10}
                  max={100}
                />
              </div>
            </div>

            {/* Nutritional Requirements */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-slate-900">Nutritional Requirements</h4>
                <select
                  value={formData.requirements.lifestage}
                  onChange={(e) => handleLifestageChange(e.target.value)}
                  className="text-sm input border-gray-300 shadow-sm"
                >
                  <option key="maintenance-1753461297312" value="maintenance">Maintenance</option>
                  <option key="breeding-1753461297312" value="breeding">Breeding</option>
                  <option key="molting-1753461297312" value="molting">Molting</option>
                  <option key="juvenile-1753461297312" value="juvenile">Juvenile</option>
                </select>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <DecimalInput
                    label="Min Protein %"
                    value={formData.requirements.minProteinPercentage}
                    onChange={(value) => setFormData((prev) => ({
                      ...prev,
                      requirements: { ...prev.requirements, minProteinPercentage: value ?? 0 },
                    }))}
                    min={0}
                  />
                  <DecimalInput
                    label="Max Protein %"
                    value={formData.requirements.maxProteinPercentage}
                    onChange={(value) => setFormData((prev) => ({
                      ...prev,
                      requirements: { ...prev.requirements, maxProteinPercentage: value ?? 0 },
                    }))}
                    min={0}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <DecimalInput
                    label="Min Fat %"
                    value={formData.requirements.minFatPercentage}
                    onChange={(value) => setFormData((prev) => ({
                      ...prev,
                      requirements: { ...prev.requirements, minFatPercentage: value ?? 0 },
                    }))}
                    min={0}
                  />
                  <DecimalInput
                    label="Max Fat %"
                    value={formData.requirements.maxFatPercentage}
                    onChange={(value) => setFormData((prev) => ({
                      ...prev,
                      requirements: { ...prev.requirements, maxFatPercentage: value ?? 0 },
                    }))}
                    min={0}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <DecimalInput
                    label="Max Fiber %"
                    value={formData.requirements.maxFiberPercentage}
                    onChange={(value) => setFormData((prev) => ({
                      ...prev,
                      requirements: { ...prev.requirements, maxFiberPercentage: value ?? 0 },
                    }))}
                    min={0}
                  />
                  <DecimalInput
                    label="Max Moisture %"
                    value={formData.requirements.maxMoisturePercentage}
                    onChange={(value) => setFormData((prev) => ({
                      ...prev,
                      requirements: { ...prev.requirements, maxMoisturePercentage: value ?? 0 },
                    }))}
                    min={0}
                  />
                </div>

                <DecimalInput
                  label="Min Calories per kg"
                  value={formData.requirements.minCaloriesPerKg}
                  onChange={(value) => setFormData((prev) => ({
                    ...prev,
                    requirements: { ...prev.requirements, minCaloriesPerKg: value ?? 0 },
                  }))}
                  min={0}
                />

                <div>
                  <label className="block text-sm font-medium text-slate-700">Bird Species</label>
                  <input
                    type="text"
                    value={formData.requirements.birdSpecies}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      requirements: { ...prev.requirements, birdSpecies: e.target.value }
                    }))}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    placeholder="e.g., Mixed, Canaries, Finches"
                  />
                </div>
              </div>
            </div>

            {/* Available Feed Stocks */}
            <div className="bg-white rounded-lg shadow p-6">
              <h4 className="text-lg font-medium text-slate-900 mb-4">Available Feed Stocks</h4>
              <div className="space-y-2">
                {feedStocks.map(stock => (
                  <label key={stock.id} className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.availableFeedStockIds.includes(stock.id)}
                      onChange={() => toggleFeedStock(stock.id)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-slate-900">{stock.name}</span>
                        <span className="text-sm text-slate-500">
                          {stock.quantity} {stock.unit} @ R{Number(stock.unitPrice).toFixed(2)}/{stock.unit}
                        </span>
                      </div>
                      {stock.nutritionalInfo && (
                        <div className="text-xs text-slate-500 mt-1">
                          P: {stock.nutritionalInfo.protein || 0}g/kg | 
                          F: {stock.nutritionalInfo.fat || 0}g/kg | 
                          Fiber: {stock.nutritionalInfo.fiber || 0}g/kg
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-6">
            {optimizationResult ? (
              <>
                {/* Optimization Summary */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-lg font-medium text-slate-900">Optimization Results</h4>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="text-sm text-slate-500 hover:text-slate-700"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {optimizationResult.optimization.score.toFixed(1)}/100
                        </div>
                        <div className="text-sm text-slate-500">Optimization Score</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-cyan-600">
                          R{optimizationResult.optimization.costPerKg.toFixed(2)}
                        </div>
                        <div className="text-sm text-slate-500">Cost per kg</div>
                      </div>
                    </div>

                    <div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800">
                        Goal: {optimizationResult.optimization.goal}
                      </span>
                    </div>

                    {/* Nutritional Summary */}
                    <div>
                      <h5 className="font-medium text-slate-900 mb-2">Nutritional Profile</h5>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Protein:</span>
                          <div className="flex items-center">
                            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                              getStatusColor(getRequirementStatus(
                                optimizationResult.nutritionalSummary.proteinPercentage,
                                formData.requirements.minProteinPercentage,
                                formData.requirements.maxProteinPercentage
                              ))
                            }`}>
                              {optimizationResult.nutritionalSummary.proteinPercentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Fat:</span>
                          <div className="flex items-center">
                            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                              getStatusColor(getRequirementStatus(
                                optimizationResult.nutritionalSummary.fatPercentage,
                                formData.requirements.minFatPercentage,
                                formData.requirements.maxFatPercentage
                              ))
                            }`}>
                              {optimizationResult.nutritionalSummary.fatPercentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Fiber:</span>
                          <div className="flex items-center">
                            <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                              getStatusColor(getRequirementStatus(
                                optimizationResult.nutritionalSummary.fiberPercentage,
                                undefined,
                                formData.requirements.maxFiberPercentage
                              ))
                            }`}>
                              {optimizationResult.nutritionalSummary.fiberPercentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-500">Quality Score:</span>
                          <span className="text-sm font-medium text-slate-900">
                            {optimizationResult.nutritionalSummary.qualityScore}/100
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Optimized Mix Components */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h4 className="text-lg font-medium text-slate-900 mb-4">Optimized Feed Mix</h4>
                  <div className="space-y-3">
                    {optimizationResult.optimizedMix.map((component, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-slate-50/80 rounded-lg">
                        <div>
                          <div className="font-medium text-slate-900">{component.feedStockName}</div>
                          <div className="text-sm text-slate-500">
                            {component.quantity.toFixed(1)} {component.unit}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium text-slate-900">
                            {component.percentage.toFixed(1)}%
                          </div>
                          <div className="text-sm text-slate-500">
                            P: {component.nutritionalContribution.protein.toFixed(1)}g
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-cyan-200 bg-cyan-50/60 p-4">
                  <h4 className="text-sm font-semibold text-cyan-900">Ready to dispense?</h4>
                  <p className="mt-1 text-sm text-cyan-800">
                    Turn this optimization into a daily mix for field staff. Approved mixes appear in the BirdMan Field app for cage dispensing.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {onOpenInMixForm && (
                      <button
                        type="button"
                        onClick={handleOpenInMixForm}
                        className="btn btn-secondary text-sm focus-pop"
                      >
                        Review & edit mix
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleCreateForDispense(false)}
                      className="btn btn-secondary text-sm focus-pop"
                    >
                      Save for approval
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCreateForDispense(true)}
                      className="btn btn-primary text-sm focus-pop"
                    >
                      Create & approve for field
                    </button>
                  </div>
                </div>

                {/* Improvements */}
                {optimizationResult.optimization.improvements.length > 0 && (
                  <div className="bg-green-50 rounded-lg p-6">
                    <div className="flex items-center mb-3">
                      <LightBulbIcon className="h-5 w-5 text-green-600 mr-2" />
                      <h4 className="font-medium text-green-900">Optimization Improvements</h4>
                    </div>
                    <ul className="space-y-2">
                      {optimizationResult.optimization.improvements.map((improvement, index) => (
                        <li key={index} className="flex items-start">
                          <CheckIcon className="h-4 w-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-green-800">{improvement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-center py-12">
                  <CogIcon className="mx-auto h-12 w-12 text-slate-400" />
                  <h3 className="mt-2 text-sm font-medium text-slate-900">No Optimization Results</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    Set your requirements and click "Optimize Feed Mix" to get results.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Form Actions */}
        <div className="mt-6 flex justify-end space-x-3">
          {isModal && onClose && (
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary text-sm"
            >
              Close
            </button>
          )}
          <button
            type="submit"
            disabled={isLoading || formData.availableFeedStockIds.length === 0}
            className="btn btn-primary text-sm inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Optimizing...
              </>
            ) : (
              <>
                <CogIcon className="h-4 w-4 mr-2" />
                Optimize Feed Mix
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );

  if (isModal) {
    return (
      <BrandModal open={!!isOpen} onClose={onClose || (() => {})} maxWidthClass="max-w-6xl">
        <BrandModalHeader
          title="Feed Mix Optimizer"
          subtitle="Optimize your feed mix based on nutritional requirements and cost constraints"
          onClose={onClose || (() => {})}
        />
        <BrandModalBody>
          {optimizerBody}
        </BrandModalBody>
      </BrandModal>
    );
  }

  return optimizerBody;
} 